import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { DownloadSimple, Trash, UploadSimple, Images, FileArrowDown, CloudArrowDown, FolderOpen } from '@phosphor-icons/react';
import { useAuth } from '../auth/AuthContext';
import ConfirmDialog from '../components/ConfirmDialog';
import DropboxFolderPicker from '../components/DropboxFolderPicker';
import { formatNzDate } from '../utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatSize = (bytes) => {
  const n = Number(bytes) || 0;
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
};

const UploadPanel = ({ onUploaded }) => {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null); // { done, total }
  const [errors, setErrors] = useState([]);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (files.length === 0) return;
    setBusy(true);
    setErrors([]);
    setProgress({ done: 0, total: files.length });
    const failed = [];
    for (const file of files) {
      const form = new FormData();
      form.append('file', file);
      try {
        await axios.post(`${API}/product-images`, form);
      } catch (e) {
        failed.push(e?.response?.data?.detail || `${file.name}: upload failed`);
      }
      setProgress((p) => ({ ...p, done: p.done + 1 }));
    }
    setErrors(failed);
    setBusy(false);
    if (inputRef.current) inputRef.current.value = '';
    onUploaded?.();
    setTimeout(() => setProgress(null), 4000);
  };

  return (
    <div className="bg-surface border border-border-color rounded-xl p-6 mb-6" data-testid="image-upload-panel">
      <h2 className="text-sm font-bold text-gold mb-1 flex items-center gap-2">
        <UploadSimple size={16} weight="bold" /> Upload product images
      </h2>
      <p className="text-xs text-text-secondary mb-3">
        Select multiple JPG / PNG / GIF / WEBP files (max 8 MB each). Files upload one at a
        time; re-uploading the same filename replaces the previous version.
      </p>
      <input
        ref={inputRef}
        type="file"
        multiple
        accept=".jpg,.jpeg,.png,.gif,.webp,image/jpeg,image/png,image/gif,image/webp"
        disabled={busy}
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="image-upload-input"
        className="block w-full text-sm text-text-secondary file:mr-4 file:px-4 file:py-2 file:rounded-lg file:border-0 file:bg-gold file:text-black file:font-bold file:cursor-pointer hover:file:bg-gold-hover disabled:opacity-50"
      />
      {progress && (
        <p className="text-xs mt-3" data-testid="image-upload-progress">
          {busy
            ? <span className="text-gold">Uploading… {progress.done} / {progress.total}</span>
            : <span className="text-success">Uploaded {progress.total - errors.length} of {progress.total} file{progress.total === 1 ? '' : 's'}.</span>}
        </p>
      )}
      {errors.length > 0 && (
        <ul className="mt-2 space-y-1" data-testid="image-upload-errors">
          {errors.map((msg, i) => (
            <li key={`${i}-${msg}`} className="text-xs text-danger bg-danger/10 border border-danger/30 rounded px-2 py-1">{msg}</li>
          ))}
        </ul>
      )}
    </div>
  );
};

const DropboxPanel = ({ onImported }) => {
  const [status, setStatus] = useState(null);
  const [folderPath, setFolderPath] = useState('');
  const [editingFolder, setEditingFolder] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);
  const pollRef = useRef(null);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dropbox/status`);
      setStatus(data);
      if (data.lastFolderPath) setFolderPath((p) => p || data.lastFolderPath);
    } catch { setStatus({ configured: false, connected: false }); }
  }, []);

  useEffect(() => {
    fetchStatus();
    const params = new URLSearchParams(window.location.search);
    if (params.get('dropbox') === 'connected') setNotice({ kind: 'ok', text: 'Dropbox connected successfully.' });
    if (params.get('dropbox') === 'error') setNotice({ kind: 'err', text: 'Dropbox connection failed — please try again.' });
    if (params.get('dropbox')) window.history.replaceState({}, '', window.location.pathname);
    return () => clearInterval(pollRef.current);
  }, [fetchStatus]);

  const pollImport = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await axios.get(`${API}/dropbox/import/status`);
        setImportStatus(data);
        if (data.state !== 'running') {
          clearInterval(pollRef.current);
          setBusy(false);
          if (data.state === 'done') onImported?.();
        }
      } catch { /* keep polling */ }
    }, 2000);
  }, [onImported]);

  const startSync = async () => {
    setBusy(true);
    setNotice(null);
    setEditingFolder(false);
    try {
      await axios.post(`${API}/dropbox/import`, { folderPath });
      setImportStatus({ state: 'running', processed: 0, total: 0 });
      pollImport();
    } catch (e) {
      setBusy(false);
      setNotice({ kind: 'err', text: e?.response?.data?.detail || 'Sync failed to start.' });
    }
  };

  const disconnect = async () => {
    await axios.delete(`${API}/dropbox/connection`);
    setImportStatus(null);
    fetchStatus();
  };

  if (!status || status.configured === false) return null;

  const displayFolder = folderPath || '/ (whole Dropbox)';

  return (
    <div className="bg-surface border border-border-color rounded-xl p-6 mb-6" data-testid="dropbox-panel">
      <h2 className="text-sm font-bold text-gold mb-1 flex items-center gap-2">
        <CloudArrowDown size={16} weight="bold" /> Product images — synced from Dropbox
      </h2>
      {notice && (
        <p className={`text-xs mb-2 px-2 py-1.5 rounded border ${notice.kind === 'ok' ? 'text-success bg-success/10 border-success/30' : 'text-danger bg-danger/10 border-danger/30'}`} data-testid="dropbox-notice">
          {notice.text}
        </p>
      )}
      {!status.connected ? (
        <>
          <p className="text-xs text-text-secondary mb-3">
            Dropbox is the source of truth for product images. Connect the Raisey&apos;s Dropbox
            account once, then hit <span className="text-gold">Sync now</span> to pull every image
            (JPG/PNG/GIF/WEBP, max 8 MB) from the chosen folder into this page.
          </p>
          <a
            href={`${API}/dropbox/auth/start`}
            data-testid="dropbox-connect-button"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-black text-sm font-bold rounded-lg hover:bg-gold-hover"
          >
            <CloudArrowDown size={16} weight="bold" /> Connect Dropbox
          </a>
        </>
      ) : status.needsReauth ? (
        <>
          <p className="text-xs mb-3 px-2 py-1.5 rounded border text-danger bg-danger/10 border-danger/30" data-testid="dropbox-reauth-notice">
            Dropbox needs to be reconnected so we can see your team folders. This
            grants an extra read-only permission (account info) required to browse
            the Dropbox Business team space.
          </p>
          <a
            href={`${API}/dropbox/auth/start`}
            data-testid="dropbox-reconnect-button"
            className="inline-flex items-center gap-2 px-4 py-2 bg-gold text-black text-sm font-bold rounded-lg hover:bg-gold-hover"
          >
            <CloudArrowDown size={16} weight="bold" /> Reconnect Dropbox
          </a>
        </>
      ) : (
        <>
          <p className="text-xs text-text-secondary mb-3">
            <span className="text-success">● Dropbox connected.</span>{' '}
            Syncing pulls new files and refreshes any file that has changed in Dropbox.
            Files already stored here with the same content are left alone.
          </p>
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <button
              onClick={startSync}
              disabled={busy}
              data-testid="dropbox-sync-button"
              className="px-4 py-2 bg-gold text-black text-sm font-bold rounded-lg hover:bg-gold-hover disabled:opacity-50 inline-flex items-center gap-2"
            >
              <CloudArrowDown size={16} weight="bold" />
              {busy ? 'Syncing…' : 'Sync now'}
            </button>
            <div className="text-xs text-text-secondary">
              Folder: <span className="text-text-primary font-mono" data-testid="dropbox-folder-display">{displayFolder}</span>{' '}
              <button
                type="button"
                onClick={() => setPickerOpen(true)}
                data-testid="dropbox-browse-button"
                className="ml-1 inline-flex items-center gap-1 text-gold hover:underline"
              >
                <FolderOpen size={12} weight="bold" /> Browse…
              </button>
              <button
                type="button"
                onClick={() => setEditingFolder((v) => !v)}
                data-testid="dropbox-change-folder-toggle"
                className="ml-2 text-gold hover:underline"
              >
                {editingFolder ? 'Cancel' : 'Type path'}
              </button>
            </div>
            <button
              onClick={disconnect}
              disabled={busy}
              data-testid="dropbox-disconnect-button"
              className="ml-auto px-3 py-2 text-xs text-text-secondary hover:text-danger disabled:opacity-50"
            >
              Disconnect
            </button>
          </div>
          {editingFolder && (
            <div className="flex flex-wrap items-center gap-2 mb-2">
              <input
                type="text"
                value={folderPath}
                onChange={(e) => setFolderPath(e.target.value)}
                placeholder="/Product Images (blank = whole Dropbox)"
                disabled={busy}
                data-testid="dropbox-folder-input"
                className="flex-1 min-w-[220px] px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none disabled:opacity-50"
              />
              <button
                type="button"
                onClick={() => setEditingFolder(false)}
                data-testid="dropbox-folder-save"
                className="px-3 py-2 text-xs font-semibold border border-gold text-gold rounded-lg hover:bg-gold hover:text-black"
              >
                Save
              </button>
            </div>
          )}
          {importStatus && (
            <div className="mt-3 text-xs" data-testid="dropbox-import-status">
              {importStatus.state === 'running' && (
                <p className="text-gold">
                  Syncing… {importStatus.processed || 0} / {importStatus.total || '…'} images
                </p>
              )}
              {importStatus.state === 'done' && (
                <p className="text-success">
                  Sync complete — {importStatus.imported || 0} new, {importStatus.replaced || 0} updated,{' '}
                  {importStatus.unchanged || 0} unchanged
                  {(importStatus.skipped || []).length > 0 && `, ${importStatus.skipped.length} skipped`}.
                </p>
              )}
              {importStatus.state === 'error' && (
                <p className="text-danger">{importStatus.error || 'Sync failed.'}</p>
              )}
              {(importStatus.skipped || []).length > 0 && importStatus.state !== 'running' && (
                <ul className="mt-1 space-y-0.5 text-text-secondary" data-testid="dropbox-skipped-list">
                  {importStatus.skipped.slice(0, 10).map((s2) => (
                    <li key={`${s2.name}-${s2.reason}`}>Skipped {s2.name} — {s2.reason}</li>
                  ))}
                  {importStatus.skipped.length > 10 && <li>…and {importStatus.skipped.length - 10} more</li>}
                </ul>
              )}
            </div>
          )}
        </>
      )}
      <DropboxFolderPicker
        open={pickerOpen}
        initialPath={folderPath}
        onCancel={() => setPickerOpen(false)}
        onSelect={(p) => { setFolderPath(p); setPickerOpen(false); setEditingFolder(false); }}
      />
    </div>
  );
};

const ImageCard = ({ img, canManage, onDelete }) => (
  <div
    className="bg-surface border border-border-color rounded-xl overflow-hidden flex flex-col"
    data-testid={`image-card-${img.id}`}
  >
    <div className="h-40 bg-black/50 flex items-center justify-center overflow-hidden">
      <img
        src={`${API}/product-images/${img.id}/file`}
        alt={img.filename}
        loading="lazy"
        className="max-h-full max-w-full object-contain"
      />
    </div>
    <div className="p-3 flex-1 flex flex-col gap-1">
      <p className="text-xs text-text-primary font-semibold truncate" title={img.filename}>{img.filename}</p>
      <p className="text-[10px] text-text-secondary">
        {formatSize(img.size)}{img.uploadedAt ? ` · ${formatNzDate(img.uploadedAt)}` : ''}
      </p>
      <div className="mt-auto pt-2 flex items-center gap-2">
        <a
          href={`${API}/product-images/${img.id}/file?disposition=attachment`}
          data-testid={`image-download-${img.id}`}
          className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs font-semibold border border-gold text-gold rounded-lg hover:bg-gold hover:text-black transition-colors"
        >
          <DownloadSimple size={13} weight="bold" /> Download
        </a>
        {canManage && (
          <button
            onClick={() => onDelete(img)}
            data-testid={`image-delete-${img.id}`}
            aria-label={`Delete ${img.filename}`}
            className="p-1.5 text-text-secondary hover:text-danger"
          >
            <Trash size={14} weight="bold" />
          </button>
        )}
      </div>
    </div>
  </div>
);

export default function ProductImagesPage() {
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.role === 'sales_rep';
  const [images, setImages] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [showManualUpload, setShowManualUpload] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/product-images`);
      setImages(data.items || []);
      setTotalSize(data.totalSize || 0);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load images');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const runDelete = async () => {
    if (!deleteTarget) return;
    setDeleteBusy(true);
    try {
      await axios.delete(`${API}/product-images/${deleteTarget.id}`);
      setDeleteTarget(null);
      load();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Delete failed');
    } finally {
      setDeleteBusy(false);
    }
  };

  return (
    <div className="flex-1 p-8" data-testid="product-images-page">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <h1 className="text-4xl font-bold font-heading text-text-primary mb-1 flex items-center gap-3">
              <Images size={34} weight="duotone" className="text-gold" /> Download Product Images
            </h1>
            <p className="text-text-secondary">
              High-quality Raisey&apos;s product images for your website, socials and in-store displays.
            </p>
          </div>
          {images.length > 0 && (
            <a
              href={`${API}/product-images/download-all`}
              data-testid="download-all-images-button"
              className="shrink-0 flex items-center gap-2 px-4 py-2.5 bg-gold text-black font-bold rounded-lg hover:bg-gold-hover"
            >
              <FileArrowDown size={18} weight="bold" />
              Download all (.zip · {formatSize(totalSize)})
            </a>
          )}
        </div>

        {canManage && <DropboxPanel onImported={load} />}
        {canManage && (
          <div className="mb-6" data-testid="manual-upload-toggle-wrap">
            <label className="inline-flex items-center gap-2 text-xs text-text-secondary cursor-pointer select-none">
              <input
                type="checkbox"
                checked={showManualUpload}
                onChange={(e) => setShowManualUpload(e.target.checked)}
                data-testid="manual-upload-toggle"
                className="accent-gold"
              />
              Show manual upload (backup)
            </label>
            {showManualUpload && <div className="mt-3"><UploadPanel onUploaded={load} /></div>}
          </div>
        )}

        {error && (
          <div className="mb-4 px-4 py-3 bg-danger/10 border border-danger/30 text-danger rounded-lg text-sm" data-testid="product-images-error">
            {error}
          </div>
        )}

        {loading && <p className="text-text-secondary text-sm">Loading…</p>}

        {!loading && images.length === 0 && (
          <div className="bg-surface border border-border-color rounded-xl p-10 text-center" data-testid="product-images-empty">
            <Images size={40} weight="duotone" className="text-text-secondary mx-auto mb-3" />
            <p className="text-text-secondary text-sm">
              {canManage
                ? 'No images yet — connect Dropbox above and hit Sync now to pull them in.'
                : 'No product images are available yet. Check back soon.'}
            </p>
          </div>
        )}

        {!loading && images.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="product-images-grid">
            {images.map((img) => (
              <ImageCard key={img.id} img={img} canManage={canManage} onDelete={setDeleteTarget} />
            ))}
          </div>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete image?"
        message={deleteTarget ? `Delete "${deleteTarget.filename}"? Customers will no longer be able to download it.` : ''}
        confirmLabel="Delete"
        busy={deleteBusy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={runDelete}
        testIdPrefix="confirm-delete-image"
      />
    </div>
  );
}
